import { BellOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from '../../lib/cx.js';
import { initials } from '../../lib/format.js';
import type { AlarmState, Severity } from '../../lib/schemas.js';
import { SEVERITY_TOKENS, STATE_TOKENS } from '../../lib/severity.js';

const PILL =
  'inline-flex max-w-full items-center gap-1 overflow-hidden rounded-[var(--radius-pill)] px-2 ' +
  'py-[1px] text-[var(--text-sm)] font-medium leading-5 whitespace-nowrap';

export interface BadgeProps {
  children: ReactNode;
  /** Any CSS colour or token reference. */
  ink?: string;
  soft?: string;
  className?: string;
  title?: string;
}

export function Badge({ children, ink, soft, className, title }: BadgeProps) {
  return (
    <span
      className={cx(PILL, !ink && 'bg-[var(--surface-2)] text-[var(--text-2)]', className)}
      style={ink ? { color: ink, backgroundColor: soft ?? 'transparent' } : undefined}
      title={title}
    >
      {children}
    </span>
  );
}

export function Dot({ color, className }: { color: string; className?: string }) {
  return (
    <span
      aria-hidden
      className={cx('inline-block h-[6px] w-[6px] shrink-0 rounded-full', className)}
      style={{ backgroundColor: color }}
    />
  );
}

/** Severity is always colour **and** text - never colour alone. */
export function SeverityBadge({ severity, className }: { severity: Severity; className?: string }) {
  const token = SEVERITY_TOKENS[severity];
  return (
    <Badge ink={token.ink} soft={token.soft} className={className}>
      <Dot color={token.ink} />
      {token.label}
    </Badge>
  );
}

/**
 * State is the quiet channel: a dot plus a label. Suppression is a *modifier*,
 * not a state, so it rides along as a muted icon with its own tooltip rather
 * than doubling the pill's width and spilling into the next column.
 */
export function StateBadge({
  state,
  suppressed,
  className,
}: {
  state: AlarmState;
  suppressed?: boolean;
  className?: string;
}) {
  const token = STATE_TOKENS[state];
  const suppressedToken = STATE_TOKENS.suppressed;
  return (
    <span className={cx('flex min-w-0 items-center gap-1', className)}>
      <Badge ink={token.ink} soft={token.soft}>
        <Dot color={token.ink} />
        <span className="truncate">{token.label}</span>
      </Badge>
      {suppressed && (
        <span
          title="Notifications suppressed by a maintenance window"
          aria-label="Notifications suppressed"
          className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[var(--radius-sm)]"
          style={{ color: suppressedToken.ink, backgroundColor: suppressedToken.soft }}
        >
          <BellOff className="h-3 w-3" aria-hidden />
        </span>
      )}
    </span>
  );
}

/** Left-hand severity strip on an alarm row. */
export function SeverityStrip({ severity }: { severity: Severity }) {
  return (
    <span
      aria-hidden
      className="absolute left-0 top-0 h-full w-[3px]"
      style={{ backgroundColor: SEVERITY_TOKENS[severity].ink }}
    />
  );
}

export function Avatar({
  name,
  size = 20,
  title,
}: {
  name: string | null | undefined;
  size?: number;
  title?: string;
}) {
  if (!name) {
    return (
      <span
        className="inline-flex shrink-0 items-center justify-center rounded-full border border-dashed border-[var(--border-strong)] text-[var(--text-3)]"
        style={{ width: size, height: size, fontSize: size * 0.42 }}
        title={title ?? 'Unassigned'}
      >
        –
      </span>
    );
  }
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-full bg-[var(--surface-3)] font-medium text-[var(--text-2)]"
      style={{ width: size, height: size, fontSize: size * 0.42 }}
      title={title ?? name}
    >
      {initials(name)}
    </span>
  );
}

/** Monospace chip for ids, dedup keys and source types. */
export function Mono({
  children,
  className,
  title,
}: {
  children: ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <code
      title={title}
      className={cx(
        'rounded-[var(--radius-sm)] bg-[var(--surface-2)] px-1 py-[1px] font-mono text-[var(--text-sm)] text-[var(--text-2)]',
        className,
      )}
    >
      {children}
    </code>
  );
}
